const FEEDBACK_SELECTOR = '.bb-feedback-button, .bb-feedback-button.gleap-font.gl-block';
const GAP_PX = 16;
const DEFAULT_MOBILE_OFFSET_PX = 100;
const MOBILE_MQ = '(max-width: 1024px)';
const STICKY_SHOW_SELECTORS = [
  '.sticky-atc-bar.sticky-atc-bar--show',
  'sticky-atc-bar.sticky-atc-bar--show',
  'sticky-atc-bar-minimalist.sticky-atc-bar--show',
];

let expectedBottomPx = null;

function parseCssPx(value) {
  const parsed = parseFloat(String(value || '').trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function queryStickyShowElements() {
  const elements = [];
  STICKY_SHOW_SELECTORS.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => elements.push(el));
  });
  return elements;
}

function isMinimalistStickyShown() {
  return Boolean(document.querySelector('sticky-atc-bar-minimalist.sticky-atc-bar--show'));
}

function isClassicStickyShown() {
  const visibleBars = queryStickyShowElements();
  return visibleBars.some(
    (el) => el.classList.contains('sticky-atc-bar') && el.tagName.toLowerCase() !== 'sticky-atc-bar-minimalist'
  );
}

function isStickyAtcShown() {
  return queryStickyShowElements().length > 0;
}

function getMobileNavOffsetPx() {
  if (document.body.classList.contains('mobile-sticky-bar-down')) return 0;
  return parseCssPx(getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-bar-height'));
}

function getStickyBarHeightPx() {
  const docStyle = getComputedStyle(document.documentElement);
  let stickyHeight = parseCssPx(docStyle.getPropertyValue('--sticky-atc-bar-height'));

  if (!stickyHeight) {
    queryStickyShowElements().forEach((el) => {
      stickyHeight = Math.max(stickyHeight, el.offsetHeight || el.clientHeight || 0);
    });
  }

  return stickyHeight;
}

function getStickyAtcOffsetPx() {
  if (!isStickyAtcShown()) return 0;

  const stickyHeight = getStickyBarHeightPx();
  if (isMinimalistStickyShown()) return stickyHeight;
  if (isClassicStickyShown()) return getMobileNavOffsetPx() + stickyHeight;

  return getMobileNavOffsetPx() + stickyHeight;
}

function shouldDeferToSaveWithKits() {
  return document.body.classList.contains('save-with-kits-mobile-bar-enabled');
}

function applyFeedbackBottom(bottomPx) {
  expectedBottomPx = Math.max(0, Math.round(bottomPx));
  const stickyShown = isStickyAtcShown();

  document.querySelectorAll(FEEDBACK_SELECTOR).forEach((el) => {
    el.style.setProperty('transition', stickyShown ? 'none' : 'bottom 0.3s ease', 'important');
    el.style.setProperty('bottom', `${expectedBottomPx}px`, 'important');
  });
}

function syncFeedbackButtonBottom() {
  const buttons = document.querySelectorAll(FEEDBACK_SELECTOR);
  if (!buttons.length) return;

  if (!window.matchMedia(MOBILE_MQ).matches) {
    expectedBottomPx = null;
    buttons.forEach((el) => {
      el.style.removeProperty('bottom');
      el.style.removeProperty('transition');
    });
    return;
  }

  if (shouldDeferToSaveWithKits()) return;

  const style = getComputedStyle(document.documentElement);
  const kitsPopup = parseCssPx(style.getPropertyValue('--save-with-kits-popup-height'));
  const stickyAtc = getStickyAtcOffsetPx();

  const bottom = isStickyAtcShown()
    ? stickyAtc + kitsPopup + GAP_PX
    : getMobileNavOffsetPx() + kitsPopup + DEFAULT_MOBILE_OFFSET_PX + GAP_PX;

  applyFeedbackBottom(bottom);
}

function scheduleStickySync() {
  syncFeedbackButtonBottom();
  requestAnimationFrame(syncFeedbackButtonBottom);
}

function guardFeedbackButtonStyles() {
  document.querySelectorAll(FEEDBACK_SELECTOR).forEach((button) => {
    if (button.dataset.feedbackOffsetGuard === 'true') return;
    button.dataset.feedbackOffsetGuard = 'true';

    const observer = new MutationObserver(() => {
      if (expectedBottomPx == null || shouldDeferToSaveWithKits()) return;

      const current = parseCssPx(button.style.getPropertyValue('bottom'));
      if (Math.abs(current - expectedBottomPx) > 1) {
        button.style.setProperty('transition', isStickyAtcShown() ? 'none' : 'bottom 0.3s ease', 'important');
        button.style.setProperty('bottom', `${expectedBottomPx}px`, 'important');
      }
    });

    observer.observe(button, { attributes: true, attributeFilter: ['style'] });
  });
}

function observeStickyBars() {
  const onStickyChange = () => scheduleStickySync();

  const classObserver = new MutationObserver((mutations) => {
    const isRelevant = mutations.some((mutation) => {
      const target = mutation.target;
      if (!(target instanceof Element)) return false;
      return (
        target.classList.contains('sticky-atc-bar') ||
        target.matches('sticky-atc-bar, sticky-atc-bar-minimalist') ||
        target === document.body
      );
    });

    if (isRelevant) onStickyChange();
  });

  classObserver.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  document.querySelectorAll('.sticky-atc-bar, sticky-atc-bar, sticky-atc-bar-minimalist').forEach((el) => {
    el.addEventListener('transitionrun', onStickyChange, { passive: true });
    el.addEventListener('transitionend', onStickyChange, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(onStickyChange).observe(el);
    }
  });
}

function observeLateFeedbackButton() {
  if (document.querySelector(FEEDBACK_SELECTOR)) {
    guardFeedbackButtonStyles();
    syncFeedbackButtonBottom();
    return;
  }

  const observer = new MutationObserver(() => {
    if (!document.querySelector(FEEDBACK_SELECTOR)) return;
    observer.disconnect();
    guardFeedbackButtonStyles();
    syncFeedbackButtonBottom();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

let scrollSyncRaf = null;

function onScrollSync() {
  if (scrollSyncRaf != null) return;
  scrollSyncRaf = requestAnimationFrame(() => {
    scrollSyncRaf = null;
    syncFeedbackButtonBottom();
  });
}

function init() {
  observeStickyBars();
  observeLateFeedbackButton();

  document.addEventListener('sticky-atc:change', scheduleStickySync);
  window.addEventListener('resize', scheduleStickySync, { passive: true });
  window.addEventListener('scroll', onScrollSync, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleStickySync, { passive: true });
  }

  scheduleStickySync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
